import React, { useEffect, useState } from 'react';
import { Paper, TableBody, TableCell, TableHead, TableRow, Typography, TextField, Button, Grid, Alert } from '@mui/material';
import axios from '../../api/client';
import { formatAmount } from '../../utils/money';
import ScrollTable from '../ScrollTable';

/**
 * @param {function} [onDrillDown]  called with an account id when a row is clicked, to open
 *   that account's ledger. Optional: this screen is also rendered where there is nowhere to
 *   drill to, and a row that looks clickable and does nothing is worse than a plain one.
 */
const TrialBalance = ({ onDrillDown }) => {
  const [rows, setRows] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [balanceCheck, setBalanceCheck] = useState(null);

  // Fetch trial balance rows
  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const { data } = await axios.get('/accounting/reports/trial-balance', { params });
      // The endpoint now returns { rows, totals, balanced } rather than a bare array, so the
      // report can state whether the ledger actually balances instead of leaving that to be
      // worked out by hand. Older shape tolerated so a stale cached response still renders.
      const list = Array.isArray(data) ? data : (data.rows || []);
      setRows(list.filter(row => row.type && row.name)); // Ignore accounts missing name/type
      setBalanceCheck(Array.isArray(data) ? null : data);
    } catch {
      setRows([]);
      setBalanceCheck(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchRows();
    // On mount only. The button below is what refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // export as csv
  const handleExport = () => {
    const lines = [['Code','Name','Type','Debit','Credit']].concat(
      rows.map(r=>[r.code,r.name,r.type,r.debit,r.credit])
    );
    const csv = lines.map(l=>l.join(",")).join("\n");
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'trial-balance.csv';
    a.click(); window.URL.revokeObjectURL(url);
  };

  const totals = rows.reduce((tot, row) => {
    tot.debit += row.debit || 0;
    tot.credit += row.credit || 0;
    return tot;
  }, { debit: 0, credit: 0 });

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: onDrillDown ? 0 : 2 }}>Trial Balance</Typography>
      {onDrillDown && (
        <Typography variant="body2" color="text.secondary" mb={2}>
          Click a row to see what makes up its balance.
        </Typography>
      )}
      {/* Demonstrating that the ledger balances is the entire purpose of this report, and it
          never said whether it did — the two columns were printed and left to be added up. */}
      {balanceCheck && !balanceCheck.balanced && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Debits and credits differ by {formatAmount(balanceCheck.totals?.difference)}.
          An unbalanced entry has reached the ledger — this is exactly what a trial balance exists to catch.
        </Alert>
      )}
      {balanceCheck && balanceCheck.balanced && rows.length > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>Debits equal credits.</Alert>
      )}
      <Grid container spacing={2} alignItems="center" mb={2}>
        <Grid item><TextField type="date" label="Start Date" InputLabelProps={{shrink:true}} value={startDate} onChange={e=>setStartDate(e.target.value)} size="small" /></Grid>
        <Grid item><TextField type="date" label="End Date" InputLabelProps={{shrink:true}} value={endDate} onChange={e=>setEndDate(e.target.value)} size="small" /></Grid>
        <Grid item><Button onClick={fetchRows} variant="contained" disabled={loading}>Apply</Button></Grid>
        <Grid item xs />
        <Grid item><Button onClick={handleExport} variant="outlined">Export CSV</Button></Grid>
      </Grid>
      <ScrollTable size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Debit</TableCell>
              <TableCell align="right">Credit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row=>(
              <TableRow
                key={row.code}
                hover={!!onDrillDown && !!row.accountId}
                onClick={onDrillDown && row.accountId ? () => onDrillDown(row.accountId) : undefined}
                sx={onDrillDown && row.accountId ? { cursor: 'pointer' } : undefined}
                title={onDrillDown && row.accountId ? 'Open this account’s ledger' : undefined}
              >
                <TableCell>{row.code}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell align="right">{formatAmount(row.debit)}</TableCell>
                <TableCell align="right">{formatAmount(row.credit)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{formatAmount(totals.debit)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{formatAmount(totals.credit)}</TableCell>
            </TableRow>
          </TableBody>
        </ScrollTable>
    </Paper>
  );
};

export default TrialBalance;
